import { cn } from '../utilitarios/cn'

export interface SegmentoBarra {
  rotulo: string
  valor: number
  percentual?: number
  cor?: string
  onSelecionar?: () => void
}

export interface PropriedadesBarraSegmentada {
  segmentos: ReadonlyArray<SegmentoBarra>
  /** Rótulo dentro do segmento quando ele é largo o bastante; fora, na legenda. */
  mostrarRotuloInterno?: boolean
  altura?: 'sm' | 'md'
  className?: string
}

const CORES = [
  'var(--serie-1)',
  'var(--serie-2)',
  'var(--serie-3)',
  'var(--serie-4)',
  'var(--serie-5)',
  'var(--serie-6)',
] as const

/**
 * Barra empilhada — o split por unidade abaixo de cada donut da TELA-01
 * (`69,40% Aluguel / 30,60% Vendas`).
 *
 * Frestas de 2px na cor da superfície entre segmentos, pontas arredondadas em 4px
 * e rótulo interno só quando o segmento tem largura para ele — abaixo de ~14% o
 * texto sai e vai para a legenda, em vez de virar um borrão de duas letras.
 */
export function BarraSegmentada({
  segmentos,
  mostrarRotuloInterno = true,
  altura = 'md',
  className,
}: PropriedadesBarraSegmentada) {
  const total = segmentos.reduce((soma, item) => soma + item.valor, 0)

  if (total <= 0) {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <div
          className={cn('w-full rounded bg-superficie-3', altura === 'sm' ? 'h-4' : 'h-6')}
          aria-hidden="true"
        />
        <p className="text-[0.6875rem] text-conteudo-fraco">Sem dados no período.</p>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className={cn('flex w-full gap-0.5', altura === 'sm' ? 'h-4' : 'h-6')}>
        {segmentos.map((segmento, indice) => {
          const proporcao = (segmento.valor / total) * 100
          if (proporcao <= 0) return null
          const percentual = segmento.percentual ?? Math.round(proporcao * 100) / 100
          const rotuloVisivel = mostrarRotuloInterno && proporcao >= 14

          return (
            <button
              key={`${segmento.rotulo}-${indice}`}
              type="button"
              disabled={!segmento.onSelecionar}
              onClick={segmento.onSelecionar}
              title={`${segmento.rotulo}: ${segmento.valor} (${percentual.toLocaleString('pt-BR', {
                maximumFractionDigits: 2,
              })}%)`}
              style={{
                width: `${proporcao}%`,
                background: segmento.cor ?? CORES[indice % CORES.length],
              }}
              className={cn(
                'flex min-w-0 items-center justify-center overflow-hidden rounded-[4px] px-1 text-[0.625rem] font-semibold leading-none text-white',
                segmento.onSelecionar ? 'cursor-pointer hover:brightness-110' : 'cursor-default',
              )}
            >
              {rotuloVisivel ? (
                <span className="truncate">
                  {percentual.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%{' '}
                  {segmento.rotulo}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>

      {/* Legenda dos segmentos estreitos, que não couberam com rótulo interno. */}
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-[0.6875rem] text-conteudo-fraco">
        {segmentos.map((segmento, indice) => {
          const proporcao = (segmento.valor / total) * 100
          if (proporcao <= 0 || (mostrarRotuloInterno && proporcao >= 14)) return null
          return (
            <li key={`legenda-${segmento.rotulo}-${indice}`} className="flex items-center gap-1">
              <span
                aria-hidden="true"
                className="h-2 w-2 rounded-sm ring-1 ring-black/10"
                style={{ background: segmento.cor ?? CORES[indice % CORES.length] }}
              />
              {segmento.rotulo} · {segmento.valor}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
